import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';

const ImportContainer = styled.div`
  margin: 20px 0;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #ffffff;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  margin: 10px 0;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-family: monospace;
`;

const Button = styled.button`
  background-color: ${theme.colors.primary.main};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: ${theme.borderRadius.small};
  cursor: pointer;
  margin-right: 10px;
  
  &:hover {
    background-color: ${theme.colors.primary.dark};
  }
  
  &:disabled {
    background-color: #e0e0e0;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ $error?: boolean }>`
  margin-top: 10px;
  padding: 10px;
  border-radius: ${theme.borderRadius.small};
  background-color: ${props => props.$error ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.$error ? '#c62828' : '#2e7d32'};
`;

interface ResultadoImportado {
  posicion: number;
  nombre: string;
  dnf: boolean;
  dns: boolean;
  socioId: string;
}

interface ImportResultadosCarreraProps {
  socios: Array<{ id: string; display_name?: string; user_login?: string; nombre?: string; apellido?: string }>;
  onImport: (resultados: ResultadoImportado[]) => void;
}

export const ImportResultadosCarrera: React.FC<ImportResultadosCarreraProps> = ({ socios, onImport }) => {
  const [rawData, setRawData] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Función para extraer el nombre del piloto, eliminando el equipo entre corchetes
  const extraerNombrePiloto = (driverText: string): string => {
    // Eliminar cualquier texto entre corchetes (incluyendo los corchetes)
    return driverText.replace(/\s*\[.*?\]\s*$/, '').trim();
  };

  // Función para encontrar el ID del socio basado en el nombre
  const encontrarSocioId = (nombre: string): string | null => {
    // Primero intentamos una coincidencia exacta del nombre completo
    let socio = socios.find(s => 
      (s.display_name && s.display_name.toLowerCase() === nombre.toLowerCase()) ||
      (s.user_login && s.user_login.toLowerCase() === nombre.toLowerCase()) ||
      (s.nombre && s.apellido && `${s.nombre} ${s.apellido}`.toLowerCase() === nombre.toLowerCase())
    );

    // Si no encontramos coincidencia exacta, buscamos por nombre o apellido
    if (!socio) {
      const nombreLower = nombre.toLowerCase();
      socio = socios.find(s => {
        const nombreCompleto = s.display_name || (s.nombre && s.apellido ? `${s.nombre} ${s.apellido}` : '');
        return (
          (s.nombre && s.nombre.toLowerCase().includes(nombreLower)) ||
          (s.apellido && s.apellido.toLowerCase().includes(nombreLower)) ||
          (nombreCompleto && nombreCompleto.toLowerCase().includes(nombreLower))
        );
      });
    }

    return socio?.id || null;
  };

  const handleImport = () => {
    try {
      setError(null);
      setSuccess(null);

      const lineas = rawData.trim().split('\n');
      if (lineas.length < 2) {
        throw new Error('No se encontraron datos para importar');
      }

      // Procesar las líneas
      const resultados: ResultadoImportado[] = [];
      const noEncontrados: string[] = [];

      lineas.forEach((linea, index) => {
        // Saltar líneas vacías
        if (!linea.trim()) return;

        // Dividir por tabulador o múltiples espacios
        const columnas = linea.split(/\t|\s{2,}/).filter(Boolean);
        
        // La primera línea podría ser el encabezado, la saltamos
        if (index === 0 && (columnas[0] === 'Rank' || columnas[0] === 'Pos' || columnas[0] === 'Posición')) {
          return;
        }

        // Verificar que la línea tenga al menos 2 columnas (posición y nombre)
        if (columnas.length < 2) {
          console.warn(`Línea ${index + 1} ignorada: formato no válido`);
          return;
        }


        // Extraer posición (puede ser un número o DNF/DNS)
        let posicion = 0;
        const esDNF = columnas[0].toUpperCase() === 'DNF';
        const esDNS = columnas[0].toUpperCase() === 'DNS';
        
        if (esDNF || esDNS) {
          // Para DNF/DNS, usamos la longitud actual de resultados + 1 como posición
          posicion = resultados.length + 1;
        } else {
          // Intentar extraer la posición como número
          const pos = parseInt(columnas[0], 10);
          if (isNaN(pos)) {
            console.warn(`Línea ${index + 1}: posición no válida '${columnas[0]}', se usará ${resultados.length + 1}`);
            posicion = resultados.length + 1;
          } else {
            posicion = pos;
          }
        }


        // El nombre del piloto está en la segunda columna, extraerlo sin el equipo
        const nombreCompleto = extraerNombrePiloto(columnas[1]);
        
        // Buscar el ID del socio
        const socioId = encontrarSocioId(nombreCompleto);
        
        if (!socioId) {
          noEncontrados.push(nombreCompleto);
          return;
        }

        // Agregar el resultado
        resultados.push({
          posicion,
          nombre: nombreCompleto,
          dnf: esDNF,
          dns: esDNS,
          socioId
        });
      });

      if (resultados.length === 0) {
        throw new Error('No se encontraron resultados válidos para importar');
      }

      // Ordenar por posición
      resultados.sort((a, b) => a.posicion - b.posicion);

      // Llamar a la función de devolución de llamada con los resultados
      onImport(resultados);

      // Mostrar mensaje de éxito
      let mensaje = `Se importaron ${resultados.length} resultados correctamente.`;
      if (noEncontrados.length > 0) {
        mensaje += ` No se encontraron los siguientes pilotos: ${noEncontrados.join(', ')}`;
      }
      setSuccess(mensaje);

    } catch (err: any) {
      setError(`Error al importar: ${err.message}`);
      console.error('Error al importar resultados:', err);
    }
  };

  return (
    <ImportContainer>
      <h3>Importar resultados de carrera</h3>
      <p>Pega aquí los resultados copiados de la tabla de clasificación:</p>
      <TextArea 
        value={rawData} 
        onChange={(e) => setRawData(e.target.value)} 
        placeholder="Ejemplo:\n1 Piloto1 [Equipo1]\n2 Piloto2 [Equipo2]\nDNF Piloto3 [Equipo3]"
      />
      <div>
        <Button 
          onClick={handleImport} 
          disabled={!rawData.trim()}
        >
          Importar resultados
        </Button>
      </div>
      {error && <Message $error>{error}</Message>}
      {success && <Message>{success}</Message>}
    </ImportContainer>
  );
};

export default ImportResultadosCarrera;
